import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Status } from "Util/enums";

import Modal from "../../../../../../../../components/Generic/Modal";
import { Location } from "../../../../../../../../models/location";
import { Member } from "../../../../../../../../models/members/member";
import { Filters, Searches } from "../../../../../../../../models/shipeezi.table";
import { UserCompany } from "../../../../../../../../models/user.company";
import { useSubcontractorActions } from "../../hooks/useActions";
import { Content } from "./styles";

export interface AddMemberModalProps {
  isOpened: boolean;
  onClose: () => void;
  membersIdAdded: Array<string>;
  onConfirmAddMembers: (members: Array<Member>) => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpened,
  onClose,
  membersIdAdded,
  onConfirmAddMembers,
}) => {
  const { t: translate } = useTranslation();

  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Array<Member>>([]);

  const getSelectedMembersId = () => {
    const ids = selectedMembers.map((member) => String(member.id));

    if (membersIdAdded?.length) return ids.concat(membersIdAdded);

    return ids;
  };

  const handleSelect = (idsSelected: number[]) => {
    const membersSelected = [...selectedMembers, ...members].filter((member) =>
      idsSelected.includes(member.id!)
    );

    setSelectedMembers(membersSelected);
  };

  const [page, setPage] = useState<number>(0);
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { getAllMembersToSubcontractor, countMembersToSubcontractor } = useSubcontractorActions();

  const { data: membersData, isFetching: isFetchingData } = getAllMembersToSubcontractor(
    page,
    pageSize,
    searches,
    filters
  );
  const { data: membersCount, isFetching: isFetchingCount } = countMembersToSubcontractor(
    filters,
    searches
  );

  const onCloseModal = () => {
    setSelectedMembers([]);

    setFilters(undefined);
    setSearches([]);
    onClose();
  };

  useEffect(() => {
    if (membersData) {
      setMembers(membersData);
    }
  }, [membersData]);

  useEffect(() => {
    if (typeof membersCount === "number") {
      setTotal(membersCount);
    }
  }, [membersCount]);

  const columns: Column[] = [
    {
      name: "userCompany",
      title: translate("code"),
      formatText: (userCompany?: UserCompany) => userCompany?.code || "-",
      hideSearch: true,
    },
    { name: "fullName", title: translate("name"), searchIdentifier: "full_name" },
    { name: "email", title: translate("email") },
    { name: "typeOfMember", title: translate("typeOfMember"), hideSearch: true },
    {
      name: "userCompany",
      title: translate("typeOfUser"),
      formatText: (userCompany?: UserCompany) => userCompany?.typeUser || "-",
      hideSearch: true,
    },
    {
      name: "userCompany",
      title: `${translate("role")}(${translate("security").toLowerCase()})`,
      formatText: (userCompany?: UserCompany) => userCompany?.role?.name || "-",
      hideSearch: true,
    },
    {
      name: "location",
      title: translate("location"),
      formatText: (location?: Location) =>
        location?.country || location?.state || location?.suburb || location?.addressLine1 || "-",
      hideSearch: true,
    },
  ];

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [
            {
              value: Status.ACTIVE,
              label: translate("active"),
            },
            {
              value: Status.INACTIVE,
              label: translate("inactive"),
            },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  const onSubmit = () => {
    onConfirmAddMembers(selectedMembers);
    onCloseModal();
  };

  return (
    <div>
      <Modal
        open={isOpened}
        handleClose={onClose}
        title={translate("addAMember")}
        showActionButtons
        actionButtonProps={{
          onSave: onSubmit,
          onCancel: onCloseModal,
          textSave: translate("addMembers"),
        }}
      >
        <Content>
          <ShipeeziNewGrid
            data={members}
            totalItems={total}
            columns={columns}
            loadingData={isFetchingData || isFetchingCount}
            emptyData={translate("noMembersToShow")}
            selecteds={getSelectedMembersId()}
            handleSelect={handleSelect}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            onChangePageSize={setPageSize}
            onNextPage={setCurrentPage}
            onPreviousPage={setCurrentPage}
            onGoToPage={setCurrentPage}
            positionActionsColumn="right"
            generalActions={generalActions}
            showSort={false}
          />
        </Content>
      </Modal>
    </div>
  );
};

export default AddMemberModal;
